import React, { useState } from "react";
import { Link, Route, Routes, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ApplicationState } from "store";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { BASE_API_URL } from "store/Me";
import SearchInput from "components/shared/SearchInput";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import SiteKey from "./Key";
import moment from "moment";
import { UseLivlyLeaseUser } from "shared/api/LivlyUserProvider";
import { isSuperUser } from "shared/api/isSuperUser";

function SiteKeys() {
  const params = useParams<{ siteId: string }>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const user = UseLivlyLeaseUser();
  const isSuperUserPermission = isSuperUser(user);

  if (!isSuperUserPermission) {
    return (
      <div style={{ marginTop: 24, fontWeight: 500, color: "red" }}>
        Access Denied: You do not have permission to access this page
      </div>
    );
  }

  return (
    <KeysList
      siteId={params.siteId!}
      search={search}
      setSearch={setSearch}
      page={page}
      pageSize={pageSize}
      setPage={setPage}
      setPageSize={setPageSize}
    />
  );
}

function KeysList({
  siteId,
  search,
  page,
  pageSize,
  setSearch,
  setPage,
  setPageSize,
}: {
  siteId: string;
  search: string;
  page: number;
  pageSize: number;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}) {
  const { data, isError, isLoading } = useGetSiteKeys(
    siteId,
    search,
    page,
    pageSize,
  );

  return (
    <div style={{ paddingTop: 36 }}>
      <Link to="/admin/sites-legacy">Back to Sites</Link>
      <h3>Keys</h3>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {isError ? (
          <div className="mt-4 flex min-h-80 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-center text-sm text-rose-700 shadow-sm">
            There was an error loading keys.
          </div>
        ) : isLoading ? (
          <div className="mt-4 flex min-h-80 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm text-slate-500 shadow-sm">
            Loading keys...
          </div>
        ) : data ? (
          <AdminTable
            aria-label="Site keys"
            containerClassName="mt-4"
            footer={
              <AdminTablePagination
                currentPage={page}
                pageSize={pageSize}
                visibleRowCount={data.length}
                isLoading={isLoading}
                onNext={() => setPage(page + 1)}
                onPrevious={() => setPage(Math.max(0, page - 1))}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                disablePrevious={page === 0}
                disableNext={data.length < pageSize}
              />
            }
          >
            <thead>
              <tr>
                <th scope="col">Credential</th>
                <th scope="col">Badge ID</th>
                <th scope="col">Valid From</th>
                <th scope="col">Valid To</th>
                <th scope="col">Currently Assigned To</th>
                <th scope="col">Last Encoded</th>
                <th scope="col">Last Updated By</th>
              </tr>
            </thead>
            <tbody>
              {data.map((siteKey) => {
                const validFrom = siteKey.validFrom
                  ? moment(siteKey.validFrom).format("YYYY-MM-DD h:mm A")
                  : "--";
                const validTo = siteKey.validTo
                  ? moment(siteKey.validTo).format("YYYY-MM-DD h:mm A")
                  : "--";
                const encoded = siteKey.encodedTimestamp
                  ? moment(siteKey.encodedTimestamp).format(
                      "YYYY-MM-DD h:mm A",
                    )
                  : "--";
                return (
                  <tr key={siteKey.credential}>
                    <td className="whitespace-nowrap">
                      <Link to={`${siteKey.credential}`} state={{ guest: siteKey }}>
                        {siteKey.credential}
                      </Link>
                    </td>
                    <td>{siteKey.badgeId}</td>
                    <td>{validFrom}</td>
                    <td>{validTo}</td>
                    <td>{siteKey.guestId ?? "--"}</td>
                    <td>{encoded}</td>
                    <td>Unknown</td>
                  </tr>
                );
              })}
              {data.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-sm text-slate-500">
                    No keys found.
                  </td>
                </tr>
              )}
            </tbody>
          </AdminTable>
        ) : null}
      </div>
    </div>
  );
}

export interface SiteKeyResponse {
  badgeId: string;
  credential: string;
  encodedTimestamp: string;
  format: string;
  guestId: string;
  jaguarSectors: null;
  physicalKeyGroupId: null;
  siteId: string;
  validFrom: string;
  validTo: string;
}

async function getKeys(
  siteId: string,
  search: string,
  page: number,
  pageSize: number,
) {
  const urlParams = new URLSearchParams();
  urlParams.append("searchString", search);
  urlParams.append("page", page.toString());
  urlParams.append("pageSize", pageSize.toString());

  const result = await axios.get<SiteKeyResponse[]>(
    `${BASE_API_URL}/api/sites/${siteId}/physicalKeys?${urlParams.toString()}`,
  );
  return result.data;
}

function useGetSiteKeys(
  siteId: string,
  search: string,
  page: number,
  pageSize: number,
) {
  return useQuery({
    queryKey: ["keys", siteId, search, page, pageSize],
    queryFn: () => getKeys(siteId, search, page, pageSize),
  });
}

export default function SiteKeyRoutes() {
  return (
    <Routes>
      <Route index element={<SiteKeys />} />
      <Route path=":credentialId/*" element={<SiteKey />} />
    </Routes>
  );
}
